import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  PencilLine,
  Plus,
  RefreshCw,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import PlannedWorkoutPlan from '../components/PlannedWorkoutPlan';
import { dayjs, parsePlannedDate } from '../lib/utils';
import {
  formatPlannedWorkoutMinutes,
  normalizePlannedWorkoutMinutes,
  normalizePlannedWorkoutType,
} from '../lib/plannedWorkout';
import {
  useAccessToken,
  usePlannedWorkout,
  useRacePlan,
} from '../hooks/useProfile';
import {
  workflowFieldClass,
  workflowPrimaryClass,
  workflowSecondaryClass,
  workflowSmallFieldClass,
  workflowSurfaceClass,
} from '../components/WorkflowForm';

type PlannedWorkoutType = 'run' | 'strength' | 'recovery' | 'other';
type Interval = {
  effort?: string;
  recovery?: string;
  notes?: string;
  [key: string]: any;
};

type PlannedWorkoutFormValues = {
  title: string;
  planned_date: string;
  type: PlannedWorkoutType;
  distance: string;
  total_time: string;
  pace_goal: string;
  intervals: Interval[];
  details: string;
  intention: string;
};

const workoutTypes: { value: PlannedWorkoutType; label: string }[] = [
  { value: 'run', label: 'Run' },
  { value: 'strength', label: 'Strength' },
  { value: 'recovery', label: 'Recovery' },
  { value: 'other', label: 'Other' },
];

const durationOptions = Array.from({ length: 24 }, (_, index) => {
  const minutes = (index + 1) * 15;
  return {
    value: String(minutes),
    label: formatPlannedWorkoutMinutes(minutes),
  };
});
const durationOptionValues = new Set(durationOptions.map(({ value }) => value));

const intensityOptions = ['warmup', 'active', 'recovery', 'rest', 'cooldown'];
const durationTypeOptions = ['time', 'distance', 'reps', 'open'];
const durationUnitOptions = [
  'minute',
  'second',
  'mile',
  'kilometer',
  'meter',
  'yard',
  'rep',
];
const targetTypeOptions = [
  'open',
  'pace',
  'speed',
  'heart_rate',
  'cadence',
  'power',
];
const targetUnitOptions = [
  'minutes_per_mile',
  'seconds_per_kilometer',
  'bpm',
  'percent',
  'rpm',
  'watts',
];

const formatSecondsAsPace = (value: unknown): string | undefined => {
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds <= 0) return undefined;
  const roundedSeconds = Math.round(seconds);
  const minutes = Math.floor(roundedSeconds / 60);
  const remainder = roundedSeconds % 60;
  return `${minutes}:${remainder.toString().padStart(2, '0')}`;
};

const parsePaceAsSeconds = (value: unknown): number | undefined => {
  const pace = String(value || '').trim();
  if (!pace) return undefined;
  const match = pace.match(/^(\d+):([0-5]\d)$/);
  if (!match) return undefined;
  return Number(match[1]) * 60 + Number(match[2]);
};

const fieldClass = workflowFieldClass;
const smallFieldClass = workflowSmallFieldClass;

const emptyStep = (overrides: Partial<Interval> = {}): Interval => ({
  type: 'step',
  name: '',
  intensity: 'active',
  duration: { type: 'time', value: 10, unit: 'minute' },
  target: { type: 'open' },
  notes: '',
  ...overrides,
});

const emptyRepeat = (): Interval => ({
  type: 'repeat',
  repeat: 4,
  steps: [
    emptyStep({ name: 'Hard', intensity: 'active' }),
    emptyStep({
      name: 'Recovery',
      intensity: 'recovery',
      duration: { type: 'time', value: 2, unit: 'minute' },
    }),
  ],
});

const isBasicInterval = (interval: Interval) =>
  !('duration' in interval) &&
  !('steps' in interval) &&
  interval.type !== 'repeat';

const normalizeInterval = (interval: Interval): Interval => {
  if (interval.type === 'repeat') {
    return {
      type: 'repeat',
      repeat: interval.repeat || 2,
      steps: Array.isArray(interval.steps)
        ? interval.steps.map(normalizeInterval)
        : [emptyStep()],
    };
  }

  if (isBasicInterval(interval)) {
    return emptyStep({
      name: interval.effort || 'Workout step',
      notes: [interval.recovery, interval.notes].filter(Boolean).join(' '),
    });
  }

  const normalizedTarget = { type: 'open', ...(interval.target || {}) };
  if (normalizedTarget.unit === 'seconds_per_mile') {
    normalizedTarget.unit = 'minutes_per_mile';
    normalizedTarget.value = formatSecondsAsPace(normalizedTarget.value);
    normalizedTarget.low = formatSecondsAsPace(normalizedTarget.low);
    normalizedTarget.high = formatSecondsAsPace(normalizedTarget.high);
  }

  return {
    ...emptyStep(),
    ...interval,
    duration: { ...emptyStep().duration, ...(interval.duration || {}) },
    target: normalizedTarget,
  };
};

const parseIntervals = (value?: string): Interval[] => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.map(normalizeInterval);
  } catch (_error) {
    // Fall through to preserving legacy interval text as notes.
  }
  return [normalizeInterval({ effort: '', recovery: '', notes: value })];
};

const serializeInterval = (interval: Interval): Interval => {
  const normalized = normalizeInterval(interval);
  if (normalized.type === 'repeat') {
    return {
      ...normalized,
      steps: (normalized.steps || []).map(serializeInterval),
    };
  }
  if (normalized.target?.unit !== 'minutes_per_mile') return normalized;
  return {
    ...normalized,
    target: {
      ...normalized.target,
      unit: 'seconds_per_mile',
      value: parsePaceAsSeconds(normalized.target.value),
      low: parsePaceAsSeconds(normalized.target.low),
      high: parsePaceAsSeconds(normalized.target.high),
    },
  };
};

const serializeIntervals = (intervals: Interval[]) =>
  JSON.stringify(intervals.map(serializeInterval));

const updateStepField = (
  step: Interval,
  field: string,
  value: string | number,
): Interval => ({ ...step, [field]: value });

const updateNestedField = (
  step: Interval,
  parent: 'duration' | 'target',
  field: string,
  value: string | number | undefined,
): Interval => ({
  ...step,
  [parent]: {
    ...(step[parent] || {}),
    [field]: value,
  },
});

const numberOrUndefined = (value: string) =>
  value === '' ? undefined : Number(value);

const StructuredStepEditor = ({
  step,
  onChange,
  onRemove,
  title,
}: {
  step: Interval;
  onChange: (step: Interval) => void;
  onRemove?: () => void;
  title: string;
}) => {
  const normalized = normalizeInterval(step);
  return (
    <div className="rounded-xl border border-gray-200 p-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-bold text-gray-700">{title}</p>
        {onRemove ? (
          <button
            type="button"
            onClick={onRemove}
            className="text-red-600"
            aria-label={`Remove ${title}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      <label className="mt-3 block text-xs font-semibold text-gray-600">
        Step name
        <input
          className={smallFieldClass}
          value={normalized.name || ''}
          onChange={(event) =>
            onChange(updateStepField(normalized, 'name', event.target.value))
          }
          placeholder="Warm up, Tempo, Recovery..."
        />
      </label>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="text-xs font-semibold text-gray-600">
          Intensity
          <select
            className={smallFieldClass}
            value={normalized.intensity}
            onChange={(event) =>
              onChange(
                updateStepField(normalized, 'intensity', event.target.value),
              )
            }
          >
            {intensityOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs font-semibold text-gray-600">
          Duration type
          <select
            className={smallFieldClass}
            value={normalized.duration.type}
            onChange={(event) =>
              onChange(
                updateNestedField(
                  normalized,
                  'duration',
                  'type',
                  event.target.value,
                ),
              )
            }
          >
            {durationTypeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs font-semibold text-gray-600">
          Duration value
          <input
            className={smallFieldClass}
            type="number"
            min="0"
            step="any"
            inputMode="decimal"
            value={normalized.duration.value || ''}
            onChange={(event) =>
              onChange(
                updateNestedField(
                  normalized,
                  'duration',
                  'value',
                  numberOrUndefined(event.target.value),
                ),
              )
            }
            placeholder="10"
          />
        </label>
        <label className="text-xs font-semibold text-gray-600">
          Duration unit
          <select
            className={smallFieldClass}
            value={normalized.duration.unit || 'minute'}
            onChange={(event) =>
              onChange(
                updateNestedField(
                  normalized,
                  'duration',
                  'unit',
                  event.target.value,
                ),
              )
            }
          >
            {durationUnitOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="text-xs font-semibold text-gray-600">
          Target type
          <select
            className={smallFieldClass}
            value={normalized.target?.type || 'open'}
            onChange={(event) =>
              onChange(
                updateNestedField(
                  normalized,
                  'target',
                  'type',
                  event.target.value,
                ),
              )
            }
          >
            {targetTypeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs font-semibold text-gray-600">
          Target unit
          <select
            className={smallFieldClass}
            value={normalized.target?.unit || 'minutes_per_mile'}
            onChange={(event) =>
              onChange(
                updateNestedField(
                  normalized,
                  'target',
                  'unit',
                  event.target.value,
                ),
              )
            }
          >
            {targetUnitOptions.map((option) => (
              <option key={option} value={option}>
                {option === 'minutes_per_mile' ? 'min/mi' : option}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs font-semibold text-gray-600">
          {normalized.target?.unit === 'minutes_per_mile'
            ? 'Target low (min/mi)'
            : 'Target low'}
          <input
            className={smallFieldClass}
            type={
              normalized.target?.unit === 'minutes_per_mile' ? 'text' : 'number'
            }
            inputMode={
              normalized.target?.unit === 'minutes_per_mile'
                ? 'numeric'
                : 'decimal'
            }
            step={
              normalized.target?.unit === 'minutes_per_mile' ? undefined : 'any'
            }
            value={normalized.target?.low || ''}
            onChange={(event) =>
              onChange(
                updateNestedField(
                  normalized,
                  'target',
                  'low',
                  normalized.target?.unit === 'minutes_per_mile'
                    ? event.target.value
                    : numberOrUndefined(event.target.value),
                ),
              )
            }
            placeholder={
              normalized.target?.unit === 'minutes_per_mile' ? '7:30' : '480'
            }
          />
        </label>
        <label className="text-xs font-semibold text-gray-600">
          {normalized.target?.unit === 'minutes_per_mile'
            ? 'Target high (min/mi)'
            : 'Target high'}
          <input
            className={smallFieldClass}
            type={
              normalized.target?.unit === 'minutes_per_mile' ? 'text' : 'number'
            }
            inputMode={
              normalized.target?.unit === 'minutes_per_mile'
                ? 'numeric'
                : 'decimal'
            }
            step={
              normalized.target?.unit === 'minutes_per_mile' ? undefined : 'any'
            }
            value={normalized.target?.high || ''}
            onChange={(event) =>
              onChange(
                updateNestedField(
                  normalized,
                  'target',
                  'high',
                  normalized.target?.unit === 'minutes_per_mile'
                    ? event.target.value
                    : numberOrUndefined(event.target.value),
                ),
              )
            }
            placeholder={
              normalized.target?.unit === 'minutes_per_mile' ? '8:00' : '520'
            }
          />
        </label>
      </div>

      <label className="mt-3 block text-xs font-semibold text-gray-600">
        Notes
        <textarea
          className={smallFieldClass}
          rows={2}
          value={normalized.notes || ''}
          onChange={(event) =>
            onChange(updateStepField(normalized, 'notes', event.target.value))
          }
          placeholder="Cue or description for this step"
        />
      </label>
    </div>
  );
};

const StructuredIntervalEditor = ({
  interval,
  index,
  onChange,
  onRemove,
}: {
  interval: Interval;
  index: number;
  onChange: (interval: Interval) => void;
  onRemove: () => void;
}) => {
  const normalized = normalizeInterval(interval);

  if (normalized.type === 'repeat') {
    return (
      <div className="rounded-xl border border-brand-blueSoft bg-brand-blueSoft/20 p-3">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-bold text-gray-700">
            Repeat block {index + 1}
          </p>
          <button
            type="button"
            onClick={onRemove}
            className="text-red-600"
            aria-label="Remove repeat block"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
        <label className="mt-3 block text-xs font-semibold text-gray-600">
          Repeat count
          <input
            className={smallFieldClass}
            type="number"
            min="1"
            value={normalized.repeat || 1}
            onChange={(event) =>
              onChange({
                ...normalized,
                repeat: Math.max(1, Number(event.target.value) || 1),
              })
            }
          />
        </label>
        <div className="mt-3 space-y-3">
          {(normalized.steps || []).map((step: Interval, stepIndex: number) => (
            <StructuredStepEditor
              key={stepIndex}
              step={step}
              title={`Repeat step ${stepIndex + 1}`}
              onChange={(updatedStep) =>
                onChange({
                  ...normalized,
                  steps: normalized.steps.map(
                    (item: Interval, itemIndex: number) =>
                      itemIndex === stepIndex ? updatedStep : item,
                  ),
                })
              }
              onRemove={() =>
                onChange({
                  ...normalized,
                  steps: normalized.steps.filter(
                    (_item: Interval, itemIndex: number) =>
                      itemIndex !== stepIndex,
                  ),
                })
              }
            />
          ))}
          <Button
            type="button"
            variant="outline"
            className="w-full gap-2"
            onClick={() =>
              onChange({
                ...normalized,
                steps: [...(normalized.steps || []), emptyStep()],
              })
            }
          >
            <Plus className="h-4 w-4" />
            Add repeat step
          </Button>
        </div>
      </div>
    );
  }

  return (
    <StructuredStepEditor
      step={normalized}
      title={`Step ${index + 1}`}
      onChange={onChange}
      onRemove={onRemove}
    />
  );
};

const generatedWorkoutDraftKey = 'generated-planned-workout-draft';

export const PlannedWorkoutForm = ({
  mode,
}: {
  mode: 'generate' | 'manual' | 'edit' | 'duplicate';
}) => {
  const params = useParams({ strict: false }) as {
    id?: string;
  };
  const search = useSearch({ strict: false }) as {
    week?: string;
    date?: string;
  };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: accessToken } = useAccessToken();
  const { data: racePlan, isLoading } = useRacePlan();
  const { data: directWorkout, isLoading: isWorkoutLoading } =
    usePlannedWorkout(
      mode === 'edit' || mode === 'duplicate' ? params.id || '' : '',
    );
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasGeneratedWorkout, setHasGeneratedWorkout] = useState(false);
  const [generationPrompt, setGenerationPrompt] = useState('');

  const allWorkouts = useMemo(
    () => racePlan?.training_plan?.flatMap((week: any) => week.workouts) || [],
    [racePlan],
  );
  const workout =
    allWorkouts.find((item: any) => item.id === Number(params.id)) ||
    directWorkout;
  const [values, setValues] = useState<PlannedWorkoutFormValues>(() => ({
    title: '',
    planned_date: search.date || dayjs().format('YYYY-MM-DD'),
    type: 'run',
    distance: '',
    total_time: '45',
    pace_goal: '',
    intervals: [],
    details: '',
    intention: '',
  }));

  useEffect(() => {
    if (!workout) return;
    setValues({
      title: workout.title || '',
      planned_date: parsePlannedDate(workout.planned_date).format('YYYY-MM-DD'),
      type: normalizePlannedWorkoutType(workout.type),
      distance: workout.distance || '',
      total_time: normalizePlannedWorkoutMinutes(workout.total_time || '45'),
      pace_goal: workout.pace_goal || '',
      intervals: parseIntervals(workout.intervals),
      details: workout.details || '',
      intention: workout.intention || '',
    });
  }, [workout]);

  useEffect(() => {
    if (mode !== 'manual') return;
    const draft = sessionStorage.getItem(generatedWorkoutDraftKey);
    if (!draft) return;
    try {
      setValues(JSON.parse(draft));
    } catch (_error) {
      // Ignore an invalid or stale generated workout draft.
    } finally {
      sessionStorage.removeItem(generatedWorkoutDraftKey);
    }
  }, [mode]);

  const updateValue = <T extends keyof PlannedWorkoutFormValues>(
    field: T,
    value: PlannedWorkoutFormValues[T],
  ) => setValues((current) => ({ ...current, [field]: value }));

  const handleGenerateWorkout = async () => {
    if (!generationPrompt.trim()) {
      window.dispatchEvent(new CustomEvent('showToast', { detail: { message: 'Describe the kind of workout you want to generate.', type: 'error' } }));
      return;
    }

    setIsGenerating(true);
    try {
      const response = await fetch(
        `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/profile/plan/workouts/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            prompt: generationPrompt,
            planned_date: values.planned_date,
            week: search.week ? Number(search.week) : undefined,
          }),
        },
      );

      if (!response.ok) {
        throw new Error('Failed to generate planned workout');
      }

      const data = await response.json();
      setValues((current) => ({
        ...current,
        title: data.workout.title || '',
        planned_date: data.workout.planned_date || current.planned_date,
        type: normalizePlannedWorkoutType(data.workout.type),
        distance: data.workout.distance || '',
        total_time: normalizePlannedWorkoutMinutes(
          data.workout.total_time || current.total_time,
        ),
        pace_goal: data.workout.pace_goal || '',
        intervals: Array.isArray(data.workout.intervals)
          ? data.workout.intervals.map(normalizeInterval)
          : parseIntervals(data.workout.intervals),
        details: data.workout.details || '',
        intention: data.workout.intention || '',
      }));
      setHasGeneratedWorkout(true);
    } catch (error: any) {
      window.dispatchEvent(new CustomEvent('showToast', { detail: { message: error.message || 'Failed to generate planned workout', type: 'error' } }));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setIsSaving(true);
    try {
      const body = {
        ...(mode === 'edit' ? workout : {}),
        title: values.title || undefined,
        planned_date: values.planned_date,
        type: values.type,
        distance: values.distance || '0',
        total_time: values.total_time,
        pace_goal: values.pace_goal || 'N/A',
        intervals: serializeIntervals(values.intervals),
        details: values.details,
        intention: values.intention,
        week: search.week ? Number(search.week) : undefined,
      };
      const response = await fetch(
        mode === 'edit'
          ? `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/profile/plan/workouts/${workout.id}/update`
          : `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/profile/plan/workouts`,
        {
          method: mode === 'edit' ? 'PUT' : 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(body),
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to ${mode} planned workout`);
      }

      const data = mode !== 'edit' ? await response.json() : null;
      await queryClient.invalidateQueries({ queryKey: ['racePlan'] });
      await queryClient.invalidateQueries({ queryKey: ['plannedWorkouts'] });
      navigate({
        to: '/plan/workout/$id',
        params: {
          id: String(mode !== 'edit' ? data.workout.id : workout.id),
        },
        replace: true,
      });
    } catch (error: any) {
      window.dispatchEvent(new CustomEvent('showToast', { detail: { message: error.message || 'Failed to save planned workout', type: 'error' } }));
    } finally {
      setIsSaving(false);
    }
  };

  if (
    isLoading ||
    ((mode === 'edit' || mode === 'duplicate') && isWorkoutLoading)
  ) {
    return (
      <div className="surface-card p-10 text-center text-gray-500">
        Loading...
      </div>
    );
  }

  if ((mode === 'edit' || mode === 'duplicate') && !workout) {
    return (
      <div className="p-6 text-center text-gray-500">
        Planned workout not found.
      </div>
    );
  }

  return (
    <div
      className={
        mode === 'generate' && !hasGeneratedWorkout
          ? 'mx-auto w-full max-w-2xl px-4 pb-24 pt-3 sm:px-6 sm:pt-5 lg:px-8'
          : 'mx-auto max-w-4xl p-4 pb-24'
      }
    >
      {mode === 'edit' ||
      mode === 'duplicate' ||
      (mode === 'generate' && !hasGeneratedWorkout) ? (
        <Button
          variant="ghost"
          className="mb-5 -ml-3 hidden gap-2 text-muted-foreground hover:text-foreground md:flex"
          onClick={() =>
            mode === 'duplicate'
              ? navigate({
                  to: '/plan/workout/$id',
                  params: { id: params.id || '' },
                })
              : navigate({ to: '/plan' })
          }
          aria-label="Back to plan"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>
            Back to Plan
          </span>
        </Button>
      ) : null}
      <form
        onSubmit={handleSubmit}
        className={
          mode === 'generate' && !hasGeneratedWorkout
            ? `${workflowSurfaceClass} space-y-5`
            : 'surface-card space-y-5 p-5 sm:p-8'
        }
      >
        <div>
            <h1 className="text-2xl font-bold tracking-[-0.04em] text-brand-midnight dark:text-white sm:text-3xl">
            {mode === 'edit'
              ? 'Edit planned workout'
              : mode === 'duplicate'
                ? 'Duplicate planned workout'
                : mode === 'manual'
                  ? 'Create a workout manually'
                  : hasGeneratedWorkout
                    ? 'Planned workout'
                    : 'Generate a single workout'}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {mode === 'generate'
              ? hasGeneratedWorkout
                 ? 'Review your workout before saving it to your plan.'
                 : 'Describe what you want to train today and we will shape the session.'
              : mode === 'duplicate'
                ? 'Choose a date and review the copied workout before adding it to your plan.'
                : 'Use the same planned workout fields from your training plan.'}
          </p>
        </div>

        {mode === 'generate' ? (
          !hasGeneratedWorkout ? (
            <div className="space-y-5">
              <label className="block min-w-0 overflow-hidden text-xs font-medium text-muted-foreground">
                Date
                <input
                  className="mt-1.5 box-border block min-w-0 max-w-full appearance-none rounded-lg border border-border bg-background/40 px-3 py-2.5 text-sm font-normal text-foreground focus:border-brand-cobalt focus:outline-none focus:ring-2 focus:ring-brand-cobalt/20"
                  type="date"
                  required
                  value={values.planned_date}
                  onChange={(event) =>
                    updateValue('planned_date', event.target.value)
                  }
                />
              </label>
              <label className="block text-sm font-semibold text-foreground">
                Workout details
                <textarea
                  className="mt-2 block min-h-40 w-full resize-y rounded-xl border border-border bg-muted/45 px-4 py-3.5 text-base font-normal leading-6 text-foreground shadow-none placeholder:text-muted-foreground/70 focus:border-lime-400 focus:outline-none focus:ring-2 focus:ring-lime-400/20"
                  rows={6}
                  value={generationPrompt}
                  onChange={(event) => setGenerationPrompt(event.target.value)}
                  placeholder="e.g., 5x1k repeats at threshold pace with 90s rest"
                />
              </label>
              <Button
                type="button"
                className={`${workflowPrimaryClass} h-12 w-full gap-2 text-base`}
                disabled={isGenerating}
                onClick={handleGenerateWorkout}
              >
                <Sparkles className="h-4 w-4" />
                 {isGenerating ? 'Generating workout...' : 'Generate workout'}
              </Button>
              <Button
                type="button"
                variant="outline"
                className={`${workflowSecondaryClass} h-11 w-full`}
                onClick={() =>
                  navigate({ to: '/plan/workout/new/manual', search })
                }
              >
                Create manually instead
              </Button>
            </div>
          ) : null
        ) : null}

        {mode !== 'generate' ? (
          <>
            <label className="block min-w-0 overflow-hidden text-sm font-semibold text-gray-700">
              Date
              <input
                className={`${fieldClass} box-border block min-w-0 max-w-full appearance-none`}
                type="date"
                required
                value={values.planned_date}
                onChange={(event) =>
                  updateValue('planned_date', event.target.value)
                }
              />
            </label>

            <label className="block text-sm font-semibold text-gray-700">
              Workout type
              <select
                className={fieldClass}
                value={values.type}
                onChange={(event) =>
                  updateValue('type', event.target.value as PlannedWorkoutType)
                }
              >
                {workoutTypes.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="block text-sm font-semibold text-gray-700">
              Time
              <select
                className={fieldClass}
                value={values.total_time}
                onChange={(event) =>
                  updateValue('total_time', event.target.value)
                }
              >
                {values.total_time &&
                !durationOptionValues.has(values.total_time) ? (
                  <option value={values.total_time}>
                    {formatPlannedWorkoutMinutes(values.total_time)}
                  </option>
                ) : null}
                {durationOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="block text-sm font-semibold text-gray-700">
              Distance
              <input
                className={fieldClass}
                value={values.distance}
                onChange={(event) =>
                  updateValue('distance', event.target.value)
                }
                placeholder="5 mi"
              />
            </label>

            <label className="block text-sm font-semibold text-gray-700">
              Pace goal
              <input
                className={fieldClass}
                value={values.pace_goal}
                onChange={(event) =>
                  updateValue('pace_goal', event.target.value)
                }
                placeholder="8:30 /mi"
              />
            </label>

            <div>
              <div className="mb-2 flex items-center justify-between gap-3">
                <div>
                  <label className="text-sm font-semibold text-gray-700">
                    Garmin structured intervals
                  </label>
                  <p className="mt-1 text-xs text-gray-500">
                    Add steps or repeat blocks that can sync as a Garmin
                    workout.
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      updateValue('intervals', [
                        ...values.intervals,
                        emptyStep(),
                      ])
                    }
                    className="inline-flex items-center gap-1 text-sm font-bold text-brand-cobalt"
                  >
                    <Plus className="h-4 w-4" />
                    Step
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      updateValue('intervals', [
                        ...values.intervals,
                        emptyRepeat(),
                      ])
                    }
                    className="inline-flex items-center gap-1 text-sm font-bold text-brand-cobalt"
                  >
                    <Plus className="h-4 w-4" />
                    Repeat
                  </button>
                </div>
              </div>
              <div className="space-y-3">
                {values.intervals.map((interval, index) => (
                  <StructuredIntervalEditor
                    key={index}
                    interval={interval}
                    index={index}
                    onChange={(updatedInterval) =>
                      updateValue(
                        'intervals',
                        values.intervals.map((item, itemIndex) =>
                          itemIndex === index ? updatedInterval : item,
                        ),
                      )
                    }
                    onRemove={() =>
                      updateValue(
                        'intervals',
                        values.intervals.filter(
                          (_item, itemIndex) => itemIndex !== index,
                        ),
                      )
                    }
                  />
                ))}
                {values.intervals.length === 0 ? (
                  <p className="rounded-xl border border-dashed border-gray-200 p-4 text-sm text-gray-500">
                    No intervals added. Add steps or repeat blocks for Garmin
                    structured workouts.
                  </p>
                ) : null}
              </div>
            </div>

            <label className="block text-sm font-semibold text-gray-700">
              Details
              <textarea
                className={fieldClass}
                rows={4}
                value={values.details}
                onChange={(event) => updateValue('details', event.target.value)}
                placeholder="Warm up, main set, cool down..."
              />
            </label>

            <label className="block text-sm font-semibold text-gray-700">
              Intention
              <textarea
                className={fieldClass}
                rows={3}
                value={values.intention}
                onChange={(event) =>
                  updateValue('intention', event.target.value)
                }
                placeholder="Purpose of the session"
              />
            </label>
          </>
        ) : null}

        {mode === 'generate' ? (
          hasGeneratedWorkout ? (
            <div className="space-y-6 text-card-foreground">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.16em] text-muted-foreground">
                  Generated workout
                </p>
                <p className="mt-2 text-base font-bold capitalize text-card-foreground">
                  {values.title || values.type} ·{' '}
                  {formatPlannedWorkoutMinutes(values.total_time)}
                  {values.type !== 'strength' && values.distance
                    ? ` · ${values.distance}`
                    : null}
                </p>
              </div>
              <PlannedWorkoutPlan
                details={values.details}
                intervals={values.intervals}
              />
              <div className="grid grid-cols-2 gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className={`${workflowSecondaryClass} gap-2`}
                  onClick={() => {
                    sessionStorage.setItem(
                      generatedWorkoutDraftKey,
                      JSON.stringify(values),
                    );
                    navigate({ to: '/plan/workout/new/manual', search });
                  }}
                >
                  <PencilLine className="h-4 w-4" />
                  Edit
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className={`${workflowSecondaryClass} gap-2`}
                  disabled={isGenerating}
                  onClick={handleGenerateWorkout}
                >
                  <RefreshCw className="h-4 w-4" />
                  {isGenerating ? 'Regenerating...' : 'Regenerate'}
                </Button>
              </div>
              <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3 shadow-[0_-12px_30px_-20px_rgba(0,0,0,0.7)] backdrop-blur md:left-64">
                <div className="mx-auto max-w-4xl">
                  <Button
                    type="submit"
                    className={`${workflowPrimaryClass} min-h-12 text-base`}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving to plan...' : 'Save to Plan'}
                  </Button>
                </div>
              </div>
            </div>
          ) : null
        ) : null}

        {mode !== 'generate' ? (
          <div className="mt-6 flex gap-3 border-t border-gray-200 pt-4 dark:border-slate-700">
            <Button type="button" variant="outline" className={workflowSecondaryClass} onClick={() => navigate({ to: '/plan' })}>
              Cancel
            </Button>
            <Button type="submit" className={workflowPrimaryClass} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        ) : null}
      </form>
    </div>
  );
};

export default PlannedWorkoutForm;
